import type { ReactNode } from "react";

/**
 * The assistant's icon set, drawn for this UI rather than imported: stroke
 * icons on a 20-unit grid that inherit `currentColor`, so hover and disabled
 * states are pure CSS. Every icon is decorative — the enclosing control
 * carries the accessible label.
 */

interface IconProps {
  size?: number;
}

function Svg({ size = 20, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

/** A fresh conversation: a speech bubble with a spark bursting off its corner. */
export function NewChatIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M11.5 3.5h-5a4 4 0 0 0-4 4v2a4 4 0 0 0 3 3.87v2.75a.45.45 0 0 0 .74.34l2.66-2.96h3.1a4 4 0 0 0 4-4v-.75" />
      <path d="m16 1.75.66 2.09 2.09.66-2.09.66L16 7.25l-.66-2.09-2.09-.66 2.09-.66Z" />
    </Svg>
  );
}

/** A clock face — past conversations. */
export function HistoryIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="10" cy="10" r="6.5" />
      <path d="M10 6.5V10l2.5 1.5" />
    </Svg>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m5.5 5.5 9 9m0-9-9 9" />
    </Svg>
  );
}

export function CopyIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="7" y="7" width="9.5" height="9.5" rx="2" />
      <path d="M13 3.5H5.5a2 2 0 0 0-2 2V13" />
    </Svg>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m4.5 10.5 3.5 3.5 7.5-8" />
    </Svg>
  );
}

export function ChevronIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m6 8 4 4 4-4" />
    </Svg>
  );
}

export function SparkIcon(props: IconProps) {
  return (
    <Svg size={14} {...props}>
      <path d="M10 2.5 11.8 8.2 17.5 10l-5.7 1.8L10 17.5 8.2 11.8 2.5 10l5.7-1.8Z" />
    </Svg>
  );
}

/** Speedometer arc with a needle — the overview page. */
export function OverviewIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3.5 12a6.5 6.5 0 1 1 13 0" />
      <path d="m10 12 3.2-3.2" />
    </Svg>
  );
}

/** Rising trend with an arrowhead — cost drivers. */
export function DriversIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 14.5 8 9.5l3 3 6-6" />
      <path d="M13.5 6.5H17V10" />
    </Svg>
  );
}

/** A receipt with a zig-zag foot — the charges ledger. */
export function ChargesIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5.5 3.5h9v13l-2.25-1.5L10 16.5l-2.25-1.5L5.5 16.5Z" />
      <path d="M8 7.5h4M8 10.5h4" />
    </Svg>
  );
}

/** Double chevron pointing into the rail. */
export function CollapseIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m11 6-4 4 4 4M15.5 6l-4 4 4 4" />
    </Svg>
  );
}

/** Four corners pulling outward — expand to fullscreen. */
export function ExpandIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M8 3.5H3.5V8M12 3.5h4.5V8M8 16.5H3.5V12M12 16.5h4.5V12" />
    </Svg>
  );
}

/** Four corners pulling inward — shrink back from fullscreen. */
export function ContractIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3.5 8H8V3.5M16.5 8H12V3.5M3.5 12H8v4.5M16.5 12H12v4.5" />
    </Svg>
  );
}

/** A scope over a point — pick an element on screen. */
export function CrosshairIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="10" cy="10" r="5" />
      <path d="M10 2.5v3M10 14.5v3M2.5 10h3M14.5 10h3" />
    </Svg>
  );
}
